import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import HvSvg from 'src/components/HvSvg';
import macheteDux from 'src/redux/machete';
import uiDux from 'src/redux/ui';
import appDux from 'src/redux/app/';
import SelectedElementCount from 'src/components/DesignSidebar/SelectedElementCount';
import { Header } from 'src/components/Sidebar/styled';
import constants from 'src/constants';
import styled, { css } from 'styled-components';

const { MATERIALS } = constants.labels;

const BreadcrumbsContainer = styled.div`
  color: ${({ theme }) => theme.colors.defaultNavy};
  display: flex;
`;

const BreadcrumbSeparator = styled.div`
  vertical-align: middle;
  padding: 0.25rem 0;
`;

const BreadcrumbItem = styled.div`
  ${({ theme }) => theme.mixins.textEllipsis()}
  max-width: 56px;
  vertical-align: middle;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.defaultActionBlue};
  cursor: pointer;

  &:hover {
    color: ${({ theme, current }) =>
      current ? theme.colors.black : theme.colors.oceanBlue};
  }

  &.root {
    margin-left: 10px;
  }

  ${({ current, theme }) => {
    if (current) {
      return css`
        max-width: 160px;
        ${theme.mixins.font('bold', 17)}
        color: ${theme.colors.black};
        cursor: initial;
      `;
    }
  }}
`;

const mapStateToProps = (state) => ({
  selectedElementCount: macheteDux.selectors.getSelectedElementCount(state),
  breadcrumbData: appDux.selectors.getBreadcrumbData(state),
  selectedElementStyles: appDux.selectors.getUniqueSelectedElementStyles(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      handleAllProductsClick: uiDux.actions.handleAllProductsClick,
      handleDeselectAllClick: uiDux.actions.handleDeselectAllClick,
      handleDesignCategoryClick: uiDux.actions.handleDesignCategoryClick,
    },
    dispatch,
  );

export class Breadcrumbs extends PureComponent {
  static propTypes = {
    handleAllProductsClick: PropTypes.func.isRequired,
    handleDeselectAllClick: PropTypes.func.isRequired,
    handleDesignCategoryClick: PropTypes.func.isRequired,
    breadcrumbData: PropTypes.object.isRequired,
    selectedElementCount: PropTypes.number.isRequired,
    selectedElementStyles: PropTypes.object.isRequired,
  };

  renderRootLink = () => (
    <BreadcrumbItem
      role="button"
      onClick={this.props.handleAllProductsClick}
      data-selenium="breadcrumbButton"
    >
      All
    </BreadcrumbItem>
  );

  renderSeparator = () => (
    <BreadcrumbSeparator>
      <HvSvg src={require('src/assets/images/right-arrow.svg')} />
    </BreadcrumbSeparator>
  );

  renderCategoryLink = ({ category, current }) => [
    this.renderSeparator(),
    <BreadcrumbItem
      current={current}
      role="button"
      onClick={() => this.props.handleDesignCategoryClick(category)}
      data-selenium="breadcrumbButton"
    >
      {category}
    </BreadcrumbItem>,
  ];

  renderMaterialLabel = ({ materialId, current }) => [
    this.renderSeparator(),
    <BreadcrumbItem current={current}>
      {MATERIALS[materialId].name}
    </BreadcrumbItem>,
  ];

  renderGenericProductsLabel = () => [
    this.renderSeparator(),
    <BreadcrumbItem current>Products</BreadcrumbItem>,
  ];

  render() {
    const {
      handleDeselectAllClick,
      selectedElementCount,
      selectedElementStyles,
      breadcrumbData: { category, materialId },
    } = this.props;
    const elementStyleArray = Object.values(selectedElementStyles);
    let breadcrumbs;

    // show "all products" if there is no breadcrumb data and no selected elements
    if (!materialId && !category && !selectedElementCount) {
      breadcrumbs = [<span>All Products</span>];
    } else {
      breadcrumbs = [this.renderRootLink()];
      // if we have breadcrumb data set by a category or material click
      // show 3 crumbs if we have material id
      if (materialId) {
        const materialCategory = MATERIALS[materialId].category;
        breadcrumbs.push(
          this.renderCategoryLink({ category: materialCategory }),
        );
        breadcrumbs.push(
          this.renderMaterialLabel({ materialId, current: true }),
        );
        // if not material but category, show 2 crumbs
      } else if (category) {
        breadcrumbs.push(this.renderCategoryLink({ category, current: true }));
        // if no breadcrumb data, show 2 or 3 crumbs based on the selected styles.
        // what is displayed depends on whether the selected styles have
        // the same category and/or material
      } else {
        const { materialId: firstMaterialId } = elementStyleArray[0];
        const { category: firstCategory } = MATERIALS[firstMaterialId];

        const allStylesHaveSameCategory = elementStyleArray.every(
          ({ materialId: mId }) => firstCategory === MATERIALS[mId].category,
        );

        // if styles have the same category display crumb for the category
        if (allStylesHaveSameCategory) {
          breadcrumbs.push(
            this.renderCategoryLink({ category: firstCategory }),
          );
          const allStylesHaveSameMaterial = elementStyleArray.every(
            ({ materialId: mId }) => firstMaterialId === mId,
          );

          // show label for the material if styles have the same material
          if (allStylesHaveSameMaterial) {
            breadcrumbs.push(
              this.renderMaterialLabel({
                materialId: firstMaterialId,
                current: true,
              }),
            );
            // show generic products label if styles don't have the same material
          } else {
            breadcrumbs.push(this.renderGenericProductsLabel());
          }
          // show generic products label if styles don't have the same category
        } else {
          breadcrumbs.push(this.renderGenericProductsLabel());
        }
      }
    }

    return (
      <Header>
        <BreadcrumbsContainer data-selenium="breadcrumbs">
          {breadcrumbs}
        </BreadcrumbsContainer>
        {!category && !!selectedElementCount && (
          <SelectedElementCount
            handleClick={handleDeselectAllClick}
            styles={Object.values(selectedElementStyles)}
          />
        )}
      </Header>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Breadcrumbs);
